import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ClassesEntity } from '../../../../../models/entities/classes-entity';

/** Avatar colour palette — deterministic, index derived from trainer name hash. */
const AVATAR_PALETTE = ['#4a9eff', '#ff6b6b', '#51cf66', '#ffb84d', '#b47eff', '#ff8787'];

/**
 * Raw API note: the class document returned by `/classes/by-user/:id` stores
 * dates and times inside `trainingSchedule.dateRanges[]`, NOT at the top-level
 * `startDate`/`endDate`/`startTime`/`endTime` properties of `ClassesEntity`.
 * The raw objects are plain JS objects (not ClassesEntity instances), so we
 * read directly from `(cls as any).trainingSchedule.dateRanges[0]`.
 *
 * Similarly, `status` ("OnGoing" | "Upcoming" | "Completed") is a direct field
 * on the raw document and is read from `(cls as any).status` — not via the
 * ClassesEntity getter which recomputes it from startDate/endDate.
 */
@Component({
  selector: 'app-class-card',
  templateUrl: './class-card.component.html',
  styleUrls: ['./class-card.component.css'],
})
export class ClassCardComponent {
  @Input() cls!: ClassesEntity;
  /** When `false` (default), the archive action is hidden — the parent passes this
   * based on the current user's management-team membership via `AuthService.canArchive()`. */
  @Input() canArchive = false;
  @Output() cardClick = new EventEmitter<string>();
  /** Emits the class ID when the kebab-menu "Archive" action is triggered. */
  @Output() archiveClick = new EventEmitter<string>();

  // ── Status ──────────────────────────────────────────────────────────
  get statusLabel(): string {
    const stage = (this.cls as any).stage as string | undefined;
    if (!stage || stage.toLowerCase() !== 'confirm') return 'Pending';
    const labels: Record<string, string> = {
      OnGoing: 'Ongoing',
      Upcoming: 'Upcoming',
      Completed: 'Completed',
    };
    // Use raw API `status` field on the plain object
    return labels[(this.cls as any).status ?? ''] ?? 'Pending';
  }

  get statusChipClass(): string {
    const cssMap: Record<string, string> = {
      Ongoing: 'chip-ongoing',
      Upcoming: 'chip-upcoming',
      Completed: 'chip-completed',
    };
    return cssMap[this.statusLabel] ?? 'chip-pending';
  }

  // ── Meta ─────────────────────────────────────────────────────────────
  get courseName(): string {
    return (this.cls as any).trainingSchedule?.course?.name ?? 'Custom Class';
  }

  /** First date-range from the raw API `dateRanges` array. */
  private get range0(): { startDate?: string; endDate?: string; startTime?: string; endTime?: string } | null {
    const ranges = (this.cls as any).trainingSchedule?.dateRanges;
    return ranges?.length ? ranges[0] : null;
  }

  get startDate(): Date | undefined {
    const d = this.range0?.startDate;
    return d ? new Date(d) : undefined;
  }

  get endDate(): Date | undefined {
    const d = this.range0?.endDate;
    return d ? new Date(d) : undefined;
  }

  get startTime(): string | undefined {
    return this.range0?.startTime || undefined;
  }

  get endTime(): string | undefined {
    return this.range0?.endTime || undefined;
  }

  get extraRangeCount(): number {
    const ranges = (this.cls as any).trainingSchedule?.dateRanges;
    return Math.max(0, (ranges?.length ?? 1) - 1);
  }

  // ── Trainers ─────────────────────────────────────────────────────────
  get trainerAvatars(): Array<{ label: string; color: string }> {
    const trainers = (this.cls as any).trainingSchedule?.trainers ?? [];
    return trainers
      .slice(0, 3)
      .map((t: any) => ({ label: this.initials(t.name ?? t), color: this.avatarColor(t.name ?? t) }));
  }

  get trainerOverflow(): number {
    return Math.max(0, ((this.cls as any).trainingSchedule?.trainers?.length ?? 0) - 3);
  }

  // ── Participants ──────────────────────────────────────────────────────
  get participantCount(): number {
    return (this.cls as any).trainingSchedule?.participants?.length ?? 0;
  }

  // ── Actions ───────────────────────────────────────────────────────────
  formatDate(d: Date | undefined): string {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  onClick(): void {
    this.cardClick.emit(this.cls.id);
  }

  onArchiveClick(event: Event): void {
    event.stopPropagation();
    this.archiveClick.emit(this.cls.id);
  }

  private initials(name: string): string {
    if (!name?.trim()) return '?';
    return name.trim().split(/\s+/).map(w => w[0]?.toUpperCase() ?? '').slice(0, 2).join('');
  }

  private avatarColor(name: string): string {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
  }
}
